import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck } from "lucide-react";

const EMPTY = { insurance_name: "", insurance_id: "", bin_rx: "", pcn: "", grp_rx: "" };
const NEW = "__new__";
const NONE = "__none__";

export default function InsurancePicker({ onChange, testPrefix = "ins" }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [choice, setChoice] = useState(NONE);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (user && user.role === "patient") {
      api.get("/insurances/my").then(({ data }) => {
        setList(data);
        const primary = data.find((i) => i.is_primary) || data[0];
        if (primary) { setChoice(primary.id); emit(primary); }
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user]);

  function emit(ins) {
    if (!ins) return onChange(null);
    onChange({ insurance_name: ins.insurance_name, insurance_id: ins.insurance_id, bin_rx: ins.bin_rx, pcn: ins.pcn, grp_rx: ins.grp_rx });
  }

  function handleChoice(v) {
    setChoice(v);
    if (v === NONE) emit(null);
    else if (v === NEW) emit(EMPTY.insurance_name ? EMPTY : null);
    else emit(list.find((i) => i.id === v));
  }

  function setField(k) {
    return (e) => {
      const next = { ...form, [k]: e.target.value };
      setForm(next);
      emit(next);
    };
  }

  return (
    <div className="space-y-3 rounded-lg border border-primary/20 bg-secondary/40 p-4" data-testid={`${testPrefix}-insurance-section`}>
      <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4 text-primary" /> Attach insurance (optional)</div>
      <Select value={choice} onValueChange={handleChoice}>
        <SelectTrigger data-testid={`${testPrefix}-insurance-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>No insurance</SelectItem>
          {list.map((i) => (
            <SelectItem key={i.id} value={i.id}>{i.insurance_name}{i.is_primary ? " (primary)" : ""}</SelectItem>
          ))}
          <SelectItem value={NEW}>Enter new insurance</SelectItem>
        </SelectContent>
      </Select>
      {choice === NEW && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Insurance name</Label><Input data-testid={`${testPrefix}-insurance-name`} value={form.insurance_name} onChange={setField("insurance_name")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Insurance ID</Label><Input data-testid={`${testPrefix}-insurance-id`} value={form.insurance_id} onChange={setField("insurance_id")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">BIN (BinRx)</Label><Input value={form.bin_rx} onChange={setField("bin_rx")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">PCN</Label><Input value={form.pcn} onChange={setField("pcn")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Group (GrpRx)</Label><Input value={form.grp_rx} onChange={setField("grp_rx")} /></div>
        </div>
      )}
      {!user && <p className="text-xs text-muted-foreground">Log in to the patient portal to reuse your saved insurance automatically.</p>}
    </div>
  );
}
